import { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { CopyButton } from 'foundations-components/transitional/components/CopyButton';
import { Dropdown } from 'foundations-components/transitional/components/Dropdown';
import { LINK_TYPES } from 'MeetingsBase/constants/enums';
import { List } from 'foundations-components/transitional/components/List';
import { Button } from 'foundations-components/transitional/components/Button';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const isViewingOwnLinks = (selectedUserOption, meetingsUser) => meetingsUser && (!selectedUserOption || selectedUserOption === meetingsUser.userId);

// We hide certain actions (delete) on non-owned links, *unless* the user is in admin mode
export const getIsNonOwnedLink = (link, meetingsUser, selectedUserOption) => {
  if (!meetingsUser) {
    return false;
  }
  const isNonOwnedLink = link.userId !== meetingsUser.userId;
  return isViewingOwnLinks(selectedUserOption, meetingsUser) && isNonOwnedLink;
};
const MeetingsTableActions = ({
  link,
  handleEditLinkClick,
  handleDeleteLinkClick,
  handleCopyButtonClick,
  handleSetAsDefaultButtonClick,
  handleOpenEmbedModal,
  selectedUserOption,
  meetingsUser,
  canEditMoreThanOneLink,
  canEditAllLinks,
  handleOpenCloneModal,
  hasStarterFeatureAccess,
  isJitaUser
}) => {
  const history = useHistory();
  const renderedCopyButton = useMemo(() => {
    return /*#__PURE__*/_jsx(CopyButton, {
      use: "tertiary-light",
      size: "extra-small",
      value: link.link,
      onClick: handleCopyButtonClick,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "adminHeader.copyLink"
      })
    }, `${link.id}-copy-btn`);
  }, [link, handleCopyButtonClick]);
  const renderedEditButton = useMemo(() => {
    const disabledForEditPermission = !isJitaUser && selectedUserOption !== null && !isViewingOwnLinks(selectedUserOption, meetingsUser) && !canEditAllLinks;
    return /*#__PURE__*/_jsx(Tooltip, {
      disabled: !disabledForEditPermission,
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "adminHeader.editPermissionsTooltip"
      }),
      children: /*#__PURE__*/_jsx(Button, {
        "data-test-id": "edit-button",
        id: "edit-button",
        use: "tertiary-light",
        size: "extra-small",
        onClick: () => {
          handleEditLinkClick();
          history.push(`link/${link.id}`);
        },
        "data-onboarding": "edit-link-button",
        "data-selenium-test": "edit-link-button",
        disabled: disabledForEditPermission,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "adminHeader.editLink"
        })
      }, `${link.id}-edit-btn`)
    });
  }, [isJitaUser, selectedUserOption, meetingsUser, canEditAllLinks, link.id, handleEditLinkClick, history]);
  const renderedEmbedButton = useMemo(() => {
    return /*#__PURE__*/_jsx(Button, {
      "data-selenium-test": "embed-link-button",
      onClick: handleOpenEmbedModal,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "adminHeader.embedCode"
      })
    }, `${link.id}-embed-btn`);
  }, [handleOpenEmbedModal, link.id]);
  const renderedCloneButton = useMemo(() => {
    return /*#__PURE__*/_jsx(Button, {
      "data-test-id": "clone-button",
      id: "clone-button",
      onClick: handleOpenCloneModal,
      "data-selenium-test": "clone-meeting-button",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "adminHeader.cloneLink"
      })
    }, `${link.id}-clone-btn`);
  }, [handleOpenCloneModal, link.id]);
  const renderedDeleteButton = useMemo(() => {
    const disabledForEditPermission = selectedUserOption !== null && !isViewingOwnLinks(selectedUserOption, meetingsUser) && !canEditAllLinks;
    if (link.isDefaultLink) {
      return null;
    }
    return /*#__PURE__*/_jsx(Tooltip, {
      placement: "right",
      disabled: !disabledForEditPermission,
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "adminHeader.deletePermissionsTooltip"
      }),
      children: /*#__PURE__*/_jsx(Button, {
        "data-test-id": "delete-button",
        disabled: disabledForEditPermission,
        id: "delete-button",
        onClick: handleDeleteLinkClick,
        "data-selenium-test": "delete-link-button",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "adminHeader.deleteLink"
        })
      }, `${link.id}-delete-btn`)
    });
  }, [canEditAllLinks, handleDeleteLinkClick, link.id, link.isDefaultLink, meetingsUser, selectedUserOption]);
  const renderedSetAsDefaultButton = useMemo(() => {
    const disableForUpgrade = !link.isDefaultLink && !canEditMoreThanOneLink;
    if (link.isDefaultLink || selectedUserOption !== null && !isViewingOwnLinks(selectedUserOption, meetingsUser) && !canEditAllLinks) {
      return null;
    }
    return /*#__PURE__*/_jsx(Button, {
      onClick: handleSetAsDefaultButtonClick,
      "data-selenium-test": "set-as-default-button",
      disabled: disableForUpgrade,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "adminHeader.setAsDefault"
      })
    }, `${link.id}-setAsDefault-btn`);
  }, [canEditAllLinks, canEditMoreThanOneLink, handleSetAsDefaultButtonClick, link.id, link.isDefaultLink, meetingsUser, selectedUserOption]);
  const renderedOtherActionButtons = useMemo(() => {
    const isNonOwnedLink = getIsNonOwnedLink(link, meetingsUser, selectedUserOption);
    const canClone = !isJitaUser && link.type === LINK_TYPES.get('personal') && hasStarterFeatureAccess && !link.paymentLinkId;
    return [renderedEmbedButton, canClone && renderedCloneButton, isNonOwnedLink ? undefined : renderedSetAsDefaultButton, isNonOwnedLink ? undefined : renderedDeleteButton];
  }, [hasStarterFeatureAccess, isJitaUser, link, meetingsUser, renderedCloneButton, renderedDeleteButton, renderedEmbedButton, renderedSetAsDefaultButton, selectedUserOption]);
  const renderedActionDropdown = useMemo(() => {
    const dropdownButton = /*#__PURE__*/_jsx(Dropdown, {
      "data-test-id": "actions-dropdown",
      id: "action-dropdown",
      closeOnMenuClick: true,
      buttonText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "adminHeader.table.actions.dropdown"
      }),
      buttonUse: "tertiary-light",
      buttonSize: "extra-small",
      "data-selenium-test": "meetings-link-actions-dropdown",
      children: /*#__PURE__*/_jsx(List, {
        wrapChildren: true,
        children: renderedOtherActionButtons
      })
    });
    return dropdownButton;
  }, [renderedOtherActionButtons]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [renderedEditButton, renderedCopyButton, renderedActionDropdown]
  });
};
export default MeetingsTableActions;